import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Target, Zap, TrendingUp, AlertTriangle, CheckCircle, Clock, Users, Award, Sparkles, Loader2 } from 'lucide-react';
import { useConfirm } from '../../hooks/useConfirm';

const EstrategiasAutomaticas9Box = () => {
  const [selectedQuadrant, setSelectedQuadrant] = useState('high_performance_high_potential');
  const [strategies, setStrategies] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [gerandoIA, setGerandoIA] = useState(false);
  const [estrategiaIA, setEstrategiaIA] = useState(null);
  const [abaAtiva, setAbaAtiva] = useState('estrategias'); // 'estrategias' ou 'colaboradores'
  const [colaboradores, setColaboradores] = useState([]);
  const [colaboradorSelecionado, setColaboradorSelecionado] = useState(null);
  const { confirm, ConfirmDialog } = useConfirm();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const quadrants = {
    'high_performance_high_potential': {
      label: 'Futuro Líder',
      color: 'bg-gradient-to-br from-teal-500 to-teal-700',
      icon: Award
    },
    'high_performance_medium_potential': {
      label: 'Jogador Chave',
      color: 'bg-gradient-to-br from-emerald-500 to-emerald-700',
      icon: Target
    },
    'high_performance_low_potential': {
      label: 'Estrela Consistente',
      color: 'bg-gradient-to-br from-green-500 to-green-700',
      icon: CheckCircle
    },
    'medium_performance_high_potential': {
      label: 'Alto Potencial',
      color: 'bg-gradient-to-br from-cyan-500 to-cyan-700',
      icon: TrendingUp
    },
    'medium_performance_medium_potential': {
      label: 'Núcleo',
      color: 'bg-gradient-to-br from-blue-500 to-blue-700',
      icon: Users
    },
    'medium_performance_low_potential': {
      label: 'Performador Sólido',
      color: 'bg-gradient-to-br from-violet-500 to-violet-700',
      icon: CheckCircle
    },
    'low_performance_high_potential': {
      label: 'Diamante Bruto',
      color: 'bg-gradient-to-br from-amber-500 to-amber-700',
      icon: Zap
    },
    'low_performance_medium_potential': {
      label: 'Inconsistente',
      color: 'bg-gradient-to-br from-orange-500 to-orange-700',
      icon: Clock
    },
    'low_performance_low_potential': {
      label: 'Questionável',
      color: 'bg-gradient-to-br from-red-500 to-red-700',
      icon: AlertTriangle
    }
  };

  useEffect(() => {
    carregarCiclos();
  }, []);

  useEffect(() => {
    if (selectedQuadrant) {
      carregarEstrategias();
    }
  }, [selectedQuadrant]);

  useEffect(() => {
    if (selectedCycle) {
      carregarColaboradoresComEstrategias();
    }
  }, [selectedCycle]);

  const carregarCiclos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/9box/matrizes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.matrizes) {
          // Filtrar apenas matrizes ativas
          const matrizesAtivas = data.matrizes.filter(m => m.ativa);
          setCycles(matrizesAtivas);
          if (matrizesAtivas.length > 0) {
            setSelectedCycle(matrizesAtivas[0].id);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar matrizes:', error);
    }
  };

  const carregarEstrategias = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/9box/estrategias/por-quadrante/${selectedQuadrant}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setStrategies(data);
      }
    } catch (error) {
      console.error('Erro ao carregar estratégias:', error);
    }
    setLoading(false);
  };

  const carregarColaboradoresComEstrategias = async () => {
    if (!selectedCycle) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/9box/matriz/${selectedCycle}/posicoes`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setColaboradores(data.posicoes || []);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
    }
    setLoading(false);
  };

  const aplicarEstrategiasAutomaticas = async () => {
    if (!selectedCycle) {
      toast.error('Selecione uma matriz ativa');
      return;
    }

    const ok = await confirm({
      title: 'Aplicar estratégias automáticas?',
      description: '⚠️ Deseja aplicar estratégias automáticas para todos os colaboradores desta matriz?\n\nIsso irá atualizar os planos de ação de todos os colaboradores posicionados.',
      confirmLabel: 'Aplicar',
      destructive: true,
    });
    if (!ok) return;

    setApplying(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/9box/estrategias/aplicar-automaticas`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ matriz_id: selectedCycle })
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(`Estratégias aplicadas com sucesso! ${result.total_atualizado} colaboradores atualizados`);
        await carregarColaboradoresComEstrategias();
      } else {
        const error = await response.json();
        toast.error(`Erro ao aplicar estratégias: ${error.detail || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao aplicar estratégias:', error);
      toast.error('Erro ao aplicar estratégias. Verifique o console.');
    }
    setApplying(false);
  };

  const gerarEstrategiaIA = async (colaboradorId) => {
    if (!selectedCycle) {
      alert('Selecione uma matriz ativa');
      return;
    }

    setGerandoIA(true);
    setEstrategiaIA(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/9box/estrategias/gerar-ia`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            colaborador_id: colaboradorId,
            matriz_id: selectedCycle
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setEstrategiaIA(result.estrategia);
        } else {
          alert('Erro ao gerar estratégia com IA');
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Erro ao gerar estratégia com IA');
      }
    } catch (error) {
      console.error('Erro ao gerar estratégia IA:', error);
      alert('Erro ao gerar estratégia com IA');
    }
    setGerandoIA(false);
  };

  const getPriorityBadge = (priority) => {
    const config = {
      critical: { color: 'bg-red-100 text-red-800', label: 'Crítica' },
      high: { color: 'bg-orange-100 text-orange-800', label: 'Alta' },
      medium: { color: 'bg-yellow-100 text-yellow-800', label: 'Média' },
      low: { color: 'bg-blue-100 text-blue-800', label: 'Baixa' }
    };

    const priorityConfig = config[priority] || config.medium;
    
    return (
      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${priorityConfig.color}`}>
        {priorityConfig.label}
      </span>
    );
  };

  const renderAcoes = (titulo, acoes, cor) => {
    const colorMap = {
      red: { bg: 'bg-red-100', text: 'text-red-600', icon: AlertTriangle },
      yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600', icon: Clock },
      blue: { bg: 'bg-blue-100', text: 'text-blue-600', icon: TrendingUp }
    };
    const cfg = colorMap[cor] || colorMap.blue;
    const Icon = cfg.icon;
    return (
      <div className="bg-white border rounded-lg p-4">
        <h4 className="font-bold text-gray-900 mb-3 flex items-center">
          <Icon className={`w-5 h-5 mr-2 ${cfg.text}`} />
          {titulo}
        </h4>
        <ul className="space-y-2">
          {acoes.map((acao, i) => (
            <li key={i} className="flex items-start text-sm">
              <span className={`flex-shrink-0 w-6 h-6 ${cfg.bg} ${cfg.text} rounded-full flex items-center justify-center text-xs font-bold mr-2`}>
                {i + 1}
              </span>
              <span className="text-gray-700">{acao}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderEstrategiaRetencao = (retencao) => {
    if (!retencao) return null;
    return (
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h4 className="font-bold text-purple-900 mb-2">Estratégia de Retenção</h4>
        <div className="space-y-1 text-sm">
          <p><span className="font-semibold">Prioridade:</span> {retencao.priority}</p>
          <p><span className="font-semibold">Investimento:</span> {retencao.investment}</p>
          <p><span className="font-semibold">Recomendação:</span> {retencao.recommendation}</p>
        </div>
      </div>
    );
  };

  const renderConsideracoesSucessao = (sucessao) => {
    if (!sucessao) return null;
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 className="font-bold text-green-900 mb-2">Planejamento de Sucessão</h4>
        <div className="space-y-1 text-sm">
          <p><span className="font-semibold">Prontidão:</span> {sucessao.readiness}</p>
          <p><span className="font-semibold">Caminho Recomendado:</span> {sucessao.recommended_path}</p>
        </div>
      </div>
    );
  };

  const renderPlanoDesenvolvimento = (plano) => {
    if (!plano) return null;
    return (
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <h4 className="font-bold text-indigo-900 mb-2">Plano de Desenvolvimento</h4>
        <div className="space-y-1 text-sm">
          {plano.focus && <p><span className="font-semibold">Foco:</span> {plano.focus}</p>}
          {plano.timeline && <p><span className="font-semibold">Prazo:</span> {plano.timeline}</p>}
          {plano.success_metrics?.length > 0 && (
            <div>
              <p className="font-semibold">Métricas de Sucesso:</p>
              <ul className="list-disc list-inside space-y-1 mt-1">
                {plano.success_metrics.map((metric, i) => (
                  <li key={i} className="text-gray-700">{metric}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getQuadranteLabel = (quadrante) => {
    const labels = {
      1: 'Questionável',
      2: 'Inconsistente',
      3: 'Diamante Bruto',
      4: 'Performer Sólido',
      5: 'Núcleo',
      6: 'Alto Potencial',
      7: 'Estrela Consistente',
      8: 'Jogador Chave',
      9: 'Futuro Líder'
    };
    return labels[quadrante] || `Q${quadrante}`;
  };

  const getQuadranteColor = (quadrante) => {
    const colors = {
      1: 'bg-red-100 text-red-800',
      2: 'bg-orange-100 text-orange-800',
      3: 'bg-amber-100 text-amber-800',
      4: 'bg-violet-100 text-violet-800',
      5: 'bg-blue-100 text-blue-800',
      6: 'bg-cyan-100 text-cyan-800',
      7: 'bg-green-100 text-green-800',
      8: 'bg-emerald-100 text-emerald-800',
      9: 'bg-teal-100 text-teal-800'
    };
    return colors[quadrante] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Estratégias Automáticas por Quadrante</h1>
        <p className="text-gray-600 mt-2">Visualize e aplique estratégias de desenvolvimento para cada posição da matriz 9Box</p>
      </div>

      {/* Sistema de Abas */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex space-x-4">
          <button
            onClick={() => setAbaAtiva('estrategias')}
            className={`pb-4 px-4 font-medium text-sm ${
              abaAtiva === 'estrategias'
                ? 'border-b-2 border-purple-600 text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📋 Estratégias por Quadrante
          </button>
          <button
            onClick={() => setAbaAtiva('colaboradores')}
            className={`pb-4 px-4 font-medium text-sm ${
              abaAtiva === 'colaboradores'
                ? 'border-b-2 border-purple-600 text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            👥 Colaboradores e Estratégias Aplicadas ({colaboradores.length})
          </button>
        </div>
      </div>

      {/* Ação Rápida */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold mb-2">Aplicar Estratégias Automáticas</h3>
            <p className="text-purple-100">Aplique planos de ação automáticos para todos os colaboradores do ciclo ativo</p>
          </div>
          <button
            onClick={aplicarEstrategiasAutomaticas}
            disabled={applying || !selectedCycle}
            className="px-6 py-3 bg-white text-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-semibold shadow-lg disabled:opacity-50 flex items-center"
          >
            <Zap className="w-5 h-5 mr-2" />
            {applying ? 'Aplicando...' : 'Aplicar Agora'}
          </button>
        </div>
      </div>

      {/* Aba: Estratégias por Quadrante */}
      {abaAtiva === 'estrategias' && (
        <>
          {/* Grade de Quadrantes */}
          <div className="grid grid-cols-3 gap-4 mb-6">
        {Object.entries(quadrants).map(([key, quad]) => {
          const Icon = quad.icon;
          return (
            <button
              key={key}
              onClick={() => setSelectedQuadrant(key)}
              className={`${quad.color} text-white rounded-lg p-4 hover:shadow-xl transition-all transform hover:scale-105 ${
                selectedQuadrant === key ? 'ring-4 ring-purple-300 scale-105' : ''
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className="w-6 h-6" />
                {selectedQuadrant === key && (
                  <CheckCircle className="w-5 h-5" />
                )}
              </div>
              <h3 className="font-bold text-sm">{quad.label}</h3>
            </button>
          );
        })}
      </div>

      {/* Detalhes das Estratégias */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : strategies ? (
        <div className="space-y-6">
          {/* Informações do Quadrante */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{strategies.label}</h2>
                <p className="text-gray-600 mb-3">{strategies.description}</p>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Prioridade:</span>
                  {getPriorityBadge(strategies.priority)}
                </div>
              </div>
            </div>
          </div>

          {/* Ações Imediatas */}
          {strategies.immediate_actions && strategies.immediate_actions.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-red-100 rounded-lg mr-3">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Ações Imediatas</h3>
              </div>
              <ul className="space-y-2">
                {strategies.immediate_actions.map((action, index) => (
                  <li key={index} className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">
                      {index + 1}
                    </span>
                    <span className="text-gray-700">{action}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Ações de Curto Prazo */}
          {strategies.short_term_actions && strategies.short_term_actions.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-yellow-100 rounded-lg mr-3">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Ações de Curto Prazo (1-3 meses)</h3>
              </div>
              <ul className="space-y-2">
                {strategies.short_term_actions.map((action, index) => (
                  <li key={index} className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">
                      {index + 1}
                    </span>
                    <span className="text-gray-700">{action}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Ações de Longo Prazo */}
          {strategies.long_term_actions && strategies.long_term_actions.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-blue-100 rounded-lg mr-3">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Ações de Longo Prazo (6+ meses)</h3>
              </div>
              <ul className="space-y-2">
                {strategies.long_term_actions.map((action, index) => (
                  <li key={index} className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">
                      {index + 1}
                    </span>
                    <span className="text-gray-700">{action}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Estratégia de Retenção */}
          {strategies.retention_strategy && Object.keys(strategies.retention_strategy).length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-purple-100 rounded-lg mr-3">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Estratégia de Retenção</h3>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Prioridade</p>
                  {getPriorityBadge(strategies.retention_strategy.priority)}
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Investimento</p>
                  <p className="text-gray-900 font-medium capitalize">{strategies.retention_strategy.investment}</p>
                </div>
                <div className="col-span-3">
                  <p className="text-sm text-gray-500 mb-1">Recomendação</p>
                  <p className="text-gray-900">{strategies.retention_strategy.recommendation}</p>
                </div>
              </div>
            </div>
          )}

          {/* Considerações de Sucessão */}
          {strategies.succession_considerations && Object.keys(strategies.succession_considerations).length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-green-100 rounded-lg mr-3">
                  <Award className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Planejamento de Sucessão</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Prontidão</p>
                  <p className="text-gray-900 font-medium">{strategies.succession_considerations.readiness}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Caminho Recomendado</p>
                  <p className="text-gray-900">{strategies.succession_considerations.recommended_path}</p>
                </div>
              </div>
            </div>
          )}

          {/* Plano de Desenvolvimento */}
          {strategies.development_plan && Object.keys(strategies.development_plan).length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-indigo-100 rounded-lg mr-3">
                  <Target className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Plano de Desenvolvimento</h3>
              </div>
              <div className="space-y-3">
                {strategies.development_plan.focus && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Foco</p>
                    <p className="text-gray-900">{strategies.development_plan.focus}</p>
                  </div>
                )}
                {strategies.development_plan.timeline && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Prazo</p>
                    <p className="text-gray-900">{strategies.development_plan.timeline}</p>
                  </div>
                )}
                {strategies.development_plan.success_metrics && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Métricas de Sucesso</p>
                    <ul className="list-disc list-inside space-y-1">
                      {strategies.development_plan.success_metrics.map((metric, index) => (
                        <li key={index} className="text-gray-900">{metric}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Selecione um quadrante para ver as estratégias</p>
        </div>
      )}
        </>
      )}

      {/* Aba: Colaboradores e Estratégias Aplicadas */}
      {abaAtiva === 'colaboradores' && (
        <div className="space-y-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : colaboradores.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Nenhum colaborador posicionado</h3>
              <p className="text-gray-500">
                Esta matriz ainda não tem colaboradores posicionados ou estratégias aplicadas.
              </p>
            </div>
          ) : (
            <>
              {/* Resumo */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Resumo da Matriz</h2>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-600">{colaboradores.length}</p>
                    <p className="text-sm text-gray-600">Total de Colaboradores</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-3xl font-bold text-green-600">
                      {colaboradores.filter(c => c.estrategia_aplicada).length}
                    </p>
                    <p className="text-sm text-gray-600">Com Estratégia Aplicada</p>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <p className="text-3xl font-bold text-orange-600">
                      {colaboradores.filter(c => !c.estrategia_aplicada).length}
                    </p>
                    <p className="text-sm text-gray-600">Sem Estratégia</p>
                  </div>
                </div>
              </div>

              {/* Lista de Colaboradores */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b">
                  <h3 className="text-lg font-bold text-gray-900">Colaboradores e Estratégias</h3>
                  <p className="text-sm text-gray-600">Clique em um colaborador para ver detalhes da estratégia aplicada</p>
                </div>
                <div className="divide-y">
                  {colaboradores.map((colab, idx) => (
                    <div
                      key={idx}
                      className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setColaboradorSelecionado(colab)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="font-semibold text-gray-900">{colab.colaborador_nome}</h4>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getQuadranteColor(colab.quadrante)}`}>
                              {getQuadranteLabel(colab.quadrante)}
                            </span>
                            {colab.estrategia_aplicada ? (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 flex items-center">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Estratégia Aplicada
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                Sem Estratégia
                              </span>
                            )}
                          </div>
                          {colab.plano_acao_automatico && (
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">Categoria:</span> {colab.plano_acao_automatico.categoria} | 
                              <span className="ml-2 font-medium">Prioridade:</span> {colab.plano_acao_automatico.prioridade}
                            </div>
                          )}
                          {colab.colaborador_cargo && (
                            <p className="text-xs text-gray-500 mt-1">{colab.colaborador_cargo}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Performance: <span className="font-semibold">{colab.score_performance?.toFixed(1) || 'N/A'}</span></p>
                          <p className="text-sm text-gray-600">Potencial: <span className="font-semibold">{colab.score_potencial?.toFixed(1) || 'N/A'}</span></p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Modal de Detalhes do Colaborador */}
          {colaboradorSelecionado && (
            <>
              <div 
                className="fixed inset-0 bg-black bg-opacity-50 z-50" 
                onClick={() => setColaboradorSelecionado(null)}
              />
              <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{colaboradorSelecionado.colaborador_nome}</h3>
                      <p className="text-sm text-gray-600">{colaboradorSelecionado.colaborador_cargo || 'Cargo não informado'}</p>
                    </div>
                    <button
                      onClick={() => setColaboradorSelecionado(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <span className="text-2xl">×</span>
                    </button>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Informações Gerais */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Quadrante</p>
                        <p className={`text-lg font-bold mt-1 ${getQuadranteColor(colaboradorSelecionado.quadrante)}`}>
                          {getQuadranteLabel(colaboradorSelecionado.quadrante)}
                        </p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-xs text-gray-500">Performance</p>
                        <p className="text-lg font-bold text-blue-600 mt-1">
                          {colaboradorSelecionado.score_performance?.toFixed(1) || 'N/A'}
                        </p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <p className="text-xs text-gray-500">Potencial</p>
                        <p className="text-lg font-bold text-purple-600 mt-1">
                          {colaboradorSelecionado.score_potencial?.toFixed(1) || 'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* Botão Gerar Estratégia com IA */}
                    <div className="flex justify-end">
                      <button
                        onClick={() => gerarEstrategiaIA(colaboradorSelecionado.colaborador_id || colaboradorSelecionado.id)}
                        disabled={gerandoIA}
                        className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-lg hover:from-purple-700 hover:to-pink-600 transition-all disabled:opacity-50 shadow-lg"
                      >
                        {gerandoIA ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Gerando...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Gerar Estratégia com IA
                          </>
                        )}
                      </button>
                    </div>

                    {/* Estratégia Gerada por IA */}
                    {(estrategiaIA || colaboradorSelecionado.estrategia_ia) && (() => {
                      const estrategia = estrategiaIA || colaboradorSelecionado.estrategia_ia;
                      return (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                            <Sparkles className="w-5 h-5 text-purple-600" />
                            <span className="text-sm font-semibold text-purple-700">Estratégia gerada por IA</span>
                            {estrategia.gerado_em && (
                              <span className="text-xs text-purple-500 ml-auto">
                                {new Date(estrategia.gerado_em).toLocaleString('pt-BR')}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                            <div>
                              <p className="font-semibold text-purple-900">{estrategia.categoria}</p>
                              <p className="text-sm text-purple-700">Prioridade: {estrategia.prioridade}</p>
                            </div>
                          </div>

                          {estrategia.analise_comportamental && (
                            <div className="bg-white border rounded-lg p-4">
                              <h4 className="font-bold text-gray-900 mb-2">Análise Comportamental</h4>
                              <p className="text-sm text-gray-700">{estrategia.analise_comportamental}</p>
                            </div>
                          )}

                          {estrategia.acoes_imediatas?.length > 0 && renderAcoes('Ações Imediatas', estrategia.acoes_imediatas, 'red')}
                          {estrategia.acoes_curto_prazo?.length > 0 && renderAcoes('Ações Curto Prazo (1-3 meses)', estrategia.acoes_curto_prazo, 'yellow')}
                          {estrategia.acoes_longo_prazo?.length > 0 && renderAcoes('Ações Longo Prazo (6+ meses)', estrategia.acoes_longo_prazo, 'blue')}

                          {estrategia.estrategia_retencao && renderEstrategiaRetencao(estrategia.estrategia_retencao)}
                          {estrategia.consideracoes_sucessao && renderConsideracoesSucessao(estrategia.consideracoes_sucessao)}
                          {estrategia.plano_desenvolvimento && renderPlanoDesenvolvimento(estrategia.plano_desenvolvimento)}
                        </div>
                      );
                    })()}

                    {/* Estratégia Automática (fallback quando não há IA) */}
                    {!estrategiaIA && !colaboradorSelecionado.estrategia_ia && colaboradorSelecionado.plano_acao_automatico && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                          <div>
                            <p className="font-semibold text-green-900">{colaboradorSelecionado.plano_acao_automatico.categoria}</p>
                            <p className="text-sm text-green-700">Prioridade: {colaboradorSelecionado.plano_acao_automatico.prioridade}</p>
                          </div>
                          {colaboradorSelecionado.plano_acao_automatico.aplicado_em && (
                            <p className="text-xs text-green-600">
                              Aplicado em: {new Date(colaboradorSelecionado.plano_acao_automatico.aplicado_em).toLocaleDateString('pt-BR')}
                            </p>
                          )}
                        </div>

                        {colaboradorSelecionado.plano_acao_automatico.acoes_imediatas?.length > 0 && renderAcoes('Ações Imediatas', colaboradorSelecionado.plano_acao_automatico.acoes_imediatas, 'red')}
                        {colaboradorSelecionado.plano_acao_automatico.acoes_curto_prazo?.length > 0 && renderAcoes('Ações Curto Prazo (1-3 meses)', colaboradorSelecionado.plano_acao_automatico.acoes_curto_prazo, 'yellow')}
                        {colaboradorSelecionado.plano_acao_automatico.acoes_longo_prazo?.length > 0 && renderAcoes('Ações Longo Prazo (6+ meses)', colaboradorSelecionado.plano_acao_automatico.acoes_longo_prazo, 'blue')}

                        {colaboradorSelecionado.plano_acao_automatico.estrategia_retencao && renderEstrategiaRetencao(colaboradorSelecionado.plano_acao_automatico.estrategia_retencao)}
                        {colaboradorSelecionado.plano_acao_automatico.consideracoes_sucessao && renderConsideracoesSucessao(colaboradorSelecionado.plano_acao_automatico.consideracoes_sucessao)}
                      </div>
                    )}

                    {/* Nenhuma estratégia */}
                    {!gerandoIA && !estrategiaIA && !colaboradorSelecionado.estrategia_ia && !colaboradorSelecionado.plano_acao_automatico && (
                      <div className="text-center py-8">
                        <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">Nenhuma estratégia para este colaborador ainda.</p>
                        <p className="text-sm text-gray-500 mt-2">Clique em "Gerar Estratégia com IA" para criar uma estratégia personalizada.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      <ConfirmDialog />
    </div>
  );
};

export default EstrategiasAutomaticas9Box;
